import React from "react";
import { useNavigate } from "react-router-dom";
import "./AboutUsPage.css";

const AboutUsPage = () => {
  const navigate = useNavigate();

  const offerings = [
    {
      icon: "🗺️",
      title: "Personalized Itinerary Planning",
      description:
        "Create customized itineraries based on your destinations, travel dates, preferences, trip style, and desired pace.",
      items: [
        "Organize your daily travel plans",
        "Adjust schedules when plans change",
        "Customize itineraries to fit your needs",
      ],
    },
    {
      icon: "📍",
      title: "Destination Discovery",
      description:
        "Discover attractions, points of interest, and travel experiences that match your interests.",
      items: [
        "Landmarks and museums",
        "Nature and family attractions",
        "Entertainment and hidden gems",
      ],
    },
    {
      icon: "🔗",
      title: "Save and Share Itineraries",
      description:
        "Save your travel plans for future reference and share them with friends, family, or fellow travelers.",
      items: [
        "Keep trips organized in one place",
        "Return to saved plans anytime",
        "Make group planning easier",
      ],
    },
    {
      icon: "✅",
      title: "Bucket Lists",
      description:
        "Keep track of destinations you want to visit and experiences you hope to have.",
      items: [
        "Organize future travel goals",
        "Create personalized travel lists",
        "Stay inspired for future adventures",
      ],
    },
    {
      icon: "🌍",
      title: "Roam Share",
      description:
        "A travel-focused social space where travelers can share experiences and discover inspiration.",
      items: [
        "Share travel photos and videos",
        "Post stories and experiences",
        "Like, comment, and connect",
      ],
    },
    {
      icon: "✨",
      title: "Travel Inspiration",
      description:
        "Explore ideas and experiences shared by other travelers from around the world.",
      items: [
        "Discover new destinations",
        "Find memorable experiences",
        "Learn from other travelers",
      ],
    },
  ];

  const values = [
    {
      icon: "👤",
      title: "Personalization",
      description:
        "Every traveler is different. Travel plans should reflect individual interests, preferences, and goals.",
    },
    {
      icon: "🔄",
      title: "Flexibility",
      description:
        "Travel plans change. Custom Roam helps travelers adapt and customize their itineraries as needed.",
    },
    {
      icon: "💡",
      title: "Inspiration",
      description:
        "Travel should encourage exploration and discovery. We help travelers find experiences that make every journey memorable.",
    },
    {
      icon: "🤝",
      title: "Community",
      description:
        "Travel experiences become more meaningful when shared. We aim to build a welcoming community where travelers inspire one another.",
    },
    {
      icon: "🔒",
      title: "Privacy and Trust",
      description:
        "We are committed to handling user information responsibly and maintaining a platform that values privacy, security, and transparency.",
    },
  ];

  return (
    <div className="about-page">
      <header className="about-header">
        <button
          type="button"
          className="about-brand"
          onClick={() => navigate("/")}
          aria-label="Go to Custom Roam homepage"
        >
          <img
            src="/images/customroam-logo.png"
            alt="Custom Roam"
            className="about-logo"
          />
          <span>CUSTOM ROAM</span>
        </button>

        <button
          type="button"
          className="about-home-link"
          onClick={() => navigate("/")}
        >
          Back to Home
        </button>
      </header>

      <main>
        <section className="about-hero">
          <div className="about-hero-content">
            <span className="about-eyebrow">ABOUT CUSTOM ROAM</span>

            <h1>
              Plan smarter. Discover more.
              <span> Travel your way.</span>
            </h1>

            <p className="about-hero-subtitle">
              Your personalized travel planning and sharing platform.
            </p>

            <p className="about-hero-description">
              Custom Roam helps travelers create, organize, and share
              personalized travel experiences—all in one place.
            </p>

            <div className="about-hero-actions">
              <button
                type="button"
                className="about-primary-button"
                onClick={() => navigate("/")}
              >
                Plan Your Trip
              </button>

              <button
                type="button"
                className="about-secondary-button"
                onClick={() => navigate("/roam-share")}
              >
                Explore Roam Share
              </button>
            </div>
          </div>

          <div className="about-hero-visual">
            <div className="about-visual-circle about-circle-one" />
            <div className="about-visual-circle about-circle-two" />

            <div className="about-visual-card">
              <img
                src="/images/planner-map.png"
                alt="Traveler planning a personalized journey"
              />
            </div>
          </div>
        </section>

        <section className="about-section about-who-section">
          <div className="about-section-heading">
            <span>WHO WE ARE</span>
            <h2>Travel planning made simple, flexible, and inspiring</h2>
          </div>

          <div className="about-who-grid">
            <div className="about-who-content">
              <p>
                Custom Roam is a travel planning and social sharing platform
                designed to help travelers create, organize, and share
                personalized travel experiences.
              </p>

              <p>
                Whether you're planning a weekend getaway, a road trip, a
                family vacation, or an international adventure, Custom Roam
                helps you build itineraries tailored to your interests, travel
                style, and preferences.
              </p>

              <p>
                Our goal is to make travel planning more accessible while
                helping travelers discover new destinations, stay organized,
                and share memorable experiences with others.
              </p>
            </div>

            <div className="about-highlight-card">
              <span className="about-highlight-icon">✈️</span>
              <h3>One platform for your entire travel journey</h3>
              <p>
                Plan your itinerary, discover places, save your ideas, and
                share your experiences with a community of travelers.
              </p>
            </div>
          </div>
        </section>

        <section className="about-section about-mission-section">
          <div className="about-mission-card">
            <div className="about-card-icon">🎯</div>
            <div>
              <span>OUR MISSION</span>
              <h2>Helping travelers spend less time planning</h2>
              <p>
                At Custom Roam, our mission is to make travel planning more
                personalized, flexible, and enjoyable.
              </p>
              <p>
                We believe no two travelers are alike. Every trip is different,
                every traveler has unique interests, and every journey deserves
                a plan that reflects those preferences.
              </p>
              <p>
                By combining intelligent planning tools with a travel-focused
                community, we aim to help travelers spend less time planning
                and more time experiencing the world.
              </p>
            </div>
          </div>
        </section>

        <section className="about-section">
          <div className="about-section-heading about-centered-heading">
            <span>WHAT WE OFFER</span>
            <h2>Everything you need to plan and share your travels</h2>
            <p>
              From building an itinerary to sharing your favorite memories,
              Custom Roam brings travel planning and community together.
            </p>
          </div>

          <div className="about-offerings-grid">
            {offerings.map((offering) => (
              <article className="about-offering-card" key={offering.title}>
                <div className="about-offering-icon">{offering.icon}</div>
                <h3>{offering.title}</h3>
                <p>{offering.description}</p>

                <ul>
                  {offering.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="about-section about-why-section">
          <div className="about-why-content">
            <span>WHY WE BUILT CUSTOM ROAM</span>
            <h2>Travel planning should not feel overwhelming</h2>

            <p>
              Many travel tools are too generic, too rigid, or require
              travelers to switch between multiple apps and websites just to
              organize one trip.
            </p>

            <p>
              We created Custom Roam to provide a more flexible and
              personalized approach that combines planning, discovery, and
              community in one experience.
            </p>
          </div>

          <div className="about-goals-card">
            <h3>Our goal is to give travelers:</h3>

            <div className="about-goal">
              <span>01</span>
              <p>Greater control over their itineraries</p>
            </div>

            <div className="about-goal">
              <span>02</span>
              <p>Easier and more flexible trip organization</p>
            </div>

            <div className="about-goal">
              <span>03</span>
              <p>More ideas and inspiration for future travel</p>
            </div>

            <div className="about-goal">
              <span>04</span>
              <p>A platform that combines planning and community</p>
            </div>
          </div>
        </section>

        <section className="about-section">
          <div className="about-section-heading about-centered-heading">
            <span>OUR VALUES</span>
            <h2>What guides everything we build</h2>
          </div>

          <div className="about-values-grid">
            {values.map((value) => (
              <article className="about-value-card" key={value.title}>
                <div className="about-value-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="about-section about-community-section">
          <div className="about-community-visual">
            <div className="about-globe">🌍</div>
          </div>

          <div className="about-community-content">
            <span>OUR COMMUNITY</span>
            <h2>Built for travelers around the world</h2>

            <p>
              Whether you're exploring your hometown, planning your first
              international trip, or sharing experiences from your latest
              adventure, our community is here to help you discover new ideas,
              destinations, and perspectives.
            </p>

            <p>
              We believe travel has the power to connect people, broaden
              horizons, and create lasting memories.
            </p>
          </div>
        </section>

        <section className="about-section about-future-section">
          <div>
            <span>LOOKING AHEAD</span>
            <h2>Continuously improving the way people travel</h2>
            <p>
              We are continuously improving Custom Roam and exploring new ways
              to help travelers plan, discover, and share their journeys.
            </p>
            <p>
              As the platform grows, we remain committed to providing useful
              tools, meaningful experiences, and a community-driven approach
              to travel.
            </p>
          </div>

          <div className="about-future-decoration">
            <span>Plan</span>
            <span>Discover</span>
            <span>Share</span>
          </div>
        </section>

        <section className="about-contact-section">
          <div className="about-contact-content">
            <span>CONTACT US</span>
            <h2>We would love to hear from you</h2>
            <p>
              Questions, feedback, or ideas? Reach out to the Custom Roam team.
            </p>

            <div className="about-contact-links">
              <a href="mailto:admin@customroam.com">
                <span>✉️</span>
                admin@customroam.com
              </a>

              <a
                href="https://www.customroam.com"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span>🌐</span>
                www.customroam.com
              </a>
            </div>
          </div>
        </section>

        <section className="about-final-section">
          <p>Thank you for being part of the Custom Roam community.</p>
          <h2>Plan. Discover. Share. Travel Your Way.</h2>

          <button
            type="button"
            className="about-primary-button"
            onClick={() => navigate("/")}
          >
            Start Planning
          </button>
        </section>
      </main>
    </div>
  );
};

export default AboutUsPage;